/**
 * HoverTip — the app-wide hover tooltip.
 *
 * Operator direction 2026-07-06:
 *   "Hovers should be used for almost all functional / instructional
 *    copy where it makes sense to use it. Hovers should all have the
 *    same look and feel and functionality."
 *
 * Standard (SPD v5.55, 2026-02-08): mirrors the public-app pattern
 * originally in KnowledgeGraphPage.jsx L2802-2806:
 *   • bg-gray-800 surface, rounded-lg, shadow-xl
 *   • title: text-sm text-white font-bold mb-2
 *   • body:  text-xs text-gray-200 leading-relaxed
 *   • motion: opacity fade + translate-y micro-motion (200ms)
 *   • default width: 380px (wide keeps a slightly larger 420px)
 *   • z-50 above the wordmark drop-shadow glow
 *
 * Contract:
 *   • ONE component owns the visual grammar.
 *   • Uses the standard Tailwind `group` / `group-hover:` idiom so
 *     the trigger element only needs `className="relative group"` and
 *     drop this tip inside as a sibling.
 *   • `align` controls which corner of the trigger anchors the tip:
 *       "bottom-right" | "bottom-left" | "bottom-center"
 *       "top-right"    | "top-left"    | "top-center"
 *   • Pass `data-testid` on the outer div for e2e coverage.
 *
 * Dismissible mode (optional — supply all four dismissible props):
 *   • `tooltipId`      unique key persisted in hiddenTooltips
 *   • `hiddenTooltips` state map from the parent page
 *   • `onHide`         callback invoked when the user clicks Don't Show
 *   • When hiddenTooltips[tooltipId] is truthy the panel renders
 *     nothing. The parent surface is responsible for anchoring the
 *     <RestoreTooltipDot /> next to the trigger icon.
 *
 * Usage (non-dismissible — 5 existing call-sites):
 *   <div className="relative group">
 *     <button>Foo</button>
 *     <HoverTip
 *       title="Foo"
 *       description="Does the foo thing."
 *       align="bottom-right"
 *       testId="foo-hover-tip"
 *     />
 *   </div>
 *
 * Usage (dismissible — SI-Agentic surface hovers):
 *   <div className="relative group">
 *     <button>Foo</button>
 *     <HoverTip
 *       tooltipId="kg_upload"
 *       hiddenTooltips={hiddenTooltips}
 *       onHide={handleHideTooltip}
 *       title="Knowledge Graph"
 *       description="…"
 *       align="bottom-right"
 *       testId="foo-hover-tip"
 *     />
 *     {hiddenTooltips.kg_upload && (
 *       <div className="absolute -top-1 -right-1 z-10">
 *         <RestoreTooltipDot tooltipId="kg_upload" ... />
 *       </div>
 *     )}
 *   </div>
 */
import React from 'react';

const ALIGN_CLASSES = {
  'bottom-right':  'right-0 top-full',
  'bottom-left':   'left-0 top-full',
  'bottom-center': 'left-1/2 top-full -translate-x-1/2',
  'top-right':     'right-0 bottom-full',
  'top-left':      'left-0 bottom-full',
  'top-center':    'left-1/2 bottom-full -translate-x-1/2',
  // IJ-272 (2026-08-08) · horizontal placements added for the
  // Concepts spoke and any other bubble that needs the tag beside
  // it rather than above or below.
  'right-top':     'left-full top-0',
  'right-center':  'left-full top-1/2 -translate-y-1/2',
  'right-bottom':  'left-full bottom-0',
  'left-top':      'right-full top-0',
  'left-center':   'right-full top-1/2 -translate-y-1/2',
  'left-bottom':   'right-full bottom-0',
};

// IJ-274 (2026-08-08) · bridge padding on the side of the tip facing
// the trigger. The transparent padding acts as a mouse bridge so
// hovering it counts as hovering the parent `.group`. Without this
// bridge the tip disappeared the moment the mouse tried to cross
// the visual gap between trigger and tip · that made the DON'T SHOW
// button unclickable.
const BRIDGE_PAD = {
  'bottom-right':  'pt-2',
  'bottom-left':   'pt-2',
  'bottom-center': 'pt-2',
  'top-right':     'pb-2',
  'top-left':      'pb-2',
  'top-center':    'pb-2',
  'right-top':     'pl-2',
  'right-center':  'pl-2',
  'right-bottom':  'pl-2',
  'left-top':      'pr-2',
  'left-center':   'pr-2',
  'left-bottom':   'pr-2',
};

export const HoverTip = ({
  title,
  description,
  children,
  align = 'bottom-right',
  wide = false,
  // IJ-271 (2026-08-08) · smaller spoke hover tag per Round-3 direction.
  // `compact` shrinks width to 340 px · padding to p-3 · title to
  // text-base · description to text-sm. Default sizing untouched so
  // the KA + KG icon tooltips do not shift.
  compact = false,
  // D2_KC_New_Demo_R2 (2026-08-14) · tiny is 25% smaller than compact.
  // IJ-307 (2026-08-18) · rail chip rework · tiny bumped ~10 percent
  // above the original tiny sizing:
  //   width 280 (was 255) · p-2.5 (was p-2)
  //   title text-[13px] (was text-xs 12 px)
  //   description text-xs 12 px (was text-[11px])
  tiny = false,
  // D2 R3 (2026-08-14) · when the trigger sits inside a wider parent
  // (e.g. a rail card label with the card itself extending further
  // right), this pushes the tip further away from the trigger so it
  // does not overlap sibling content.
  padExtra = 0,
  // IJ-326 (2026-08-18) · yOffset shifts the tip vertically relative
  // to its anchor point. Negative lifts the tip higher, positive
  // pushes it down. Used by the tapped sub-topic rows on the pilot
  // demo left rail so each tip sits about one centimetre above the
  // row it points at.
  yOffset = 0,
  testId,
  // Dismissible mode (all three required to activate the Don't Show button):
  tooltipId,
  hiddenTooltips,
  onHide,
}) => {
  const anchor = ALIGN_CLASSES[align] || ALIGN_CLASSES['bottom-right'];
  const bridgePad = BRIDGE_PAD[align] || BRIDGE_PAD['bottom-right'];
  const dismissible = Boolean(tooltipId && hiddenTooltips && typeof onHide === 'function');

  // When dismissible + user has hidden this tip, render nothing.
  // The parent surface owns the RestoreTooltipDot placement on the trigger icon.
  if (dismissible && hiddenTooltips[tooltipId]) {
    return null;
  }

  const width = tiny ? 280 : (compact ? 340 : (wide ? 560 : 520));
  const padCls = tiny ? 'p-2.5' : (compact ? 'p-3' : 'p-4');
  const titleCls = tiny
    ? 'mb-1 text-[13px] font-bold text-white'
    : (compact
      ? 'mb-1.5 text-base font-bold text-white'
      : 'mb-2 text-xl font-bold text-white');
  const descCls = tiny
    ? 'text-xs leading-snug text-gray-200'
    : (compact
      ? 'text-sm leading-relaxed text-gray-200'
      : 'text-lg leading-relaxed text-gray-200');

  return (
    <div
      role="tooltip"
      data-testid={testId}
      className={
        'absolute ' + anchor + ' ' + bridgePad + ' ' +
        'z-50 text-left ' +
        'opacity-0 translate-y-1 pointer-events-none ' +
        'group-hover:opacity-100 group-hover:translate-y-0 ' +
        'group-hover:pointer-events-auto ' +
        'transition-all duration-200'
      }
      style={{
        width,
        marginLeft: padExtra ? `${padExtra}px` : undefined,
        marginTop: yOffset ? `${yOffset}px` : undefined,
      }}
    >
      <div className={'rounded-lg bg-gray-800 shadow-xl ' + padCls}>
        {title && (
          <p className={titleCls}>{title}</p>
        )}
        {description && (
          <p className={descCls}>
            {description}
          </p>
        )}
        {children}
        {dismissible && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onHide(tooltipId); }}
            className="mt-3 text-[10px] uppercase tracking-wider text-gray-500 hover:text-gray-300 transition-colors"
            data-testid={testId ? `${testId}-hide-btn` : `hide-tooltip-${tooltipId}`}
          >
            Don&apos;t Show
          </button>
        )}
      </div>
    </div>
  );
};

export default HoverTip;
